import { baseApi } from "common/redux/core/api.base";
import {
  ConfigService,
  ConfigurationItem,
  CreateConfigurationRequest,
  UpdateConfigurationRequest
} from "../../services/config.service";
import { ApiCacheTags } from "common/models/api-base.model";

const configApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAllConfigurations: build.query<ConfigurationItem[], void>({
      queryFn: async () => {
        try {
          const result = await ConfigService.getAllConfigurations();

          // Check if result has data property
          if (result.data) {
            return { data: result.data };
          }

          // Fallback
          if (Array.isArray(result)) {
            return { data: result };
          }

          return { data: [] };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message
            }
          };
        }
      },
      providesTags: [ApiCacheTags.CONFIGURATION_LIST]
    }),
    createConfiguration: build.mutation<
      ConfigurationItem,
      {
        configData: CreateConfigurationRequest;
        headers?: Record<string, string>;
      }
    >({
      queryFn: async ({ configData, headers = {} }) => {
        try {
          // If no headers provided or no user email in headers, extract from configData
          const userEmail =
            headers["user-email"] || configData.created_by_email;

          const result = await ConfigService.createConfiguration(
            configData,
            userEmail
          );

          if (result && result.data) {
            return { data: result.data };
          }

          // Return empty configuration if no data
          return { data: {} as ConfigurationItem };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message
            }
          };
        }
      },
      invalidatesTags: [ApiCacheTags.CONFIGURATION_LIST]
    }),
    updateConfiguration: build.mutation<
      ConfigurationItem,
      { configId: number; configData: any }
    >({
      queryFn: async ({ configId, configData }) => {
        try {
          const result = await ConfigService.updateConfiguration(configData);

          if (result.data) {
            return { data: result.data };
          }

          return { data: {} as ConfigurationItem };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message
            }
          };
        }
      },

      onQueryStarted: async (
        { configId, configData },
        { dispatch, queryFulfilled }
      ) => {
        // Optimistic update
        const patchResult = dispatch(
          configApi.util.updateQueryData(
            "getAllConfigurations",
            undefined,
            (draft) => {
              const configIndex = draft.findIndex(
                (config) => config.id === configId
              );

              if (configIndex !== -1) {
                // Update the existing configuration
                draft[configIndex] = {
                  ...draft[configIndex],
                  title: configData.config_name,
                  config_name: configData.config_name,
                  document_context: configData.document_context,
                  data_exposer: configData.data_exposer,
                  extraction_model: configData.extraction_model,
                  chat_exposure: configData.chat_exposure,
                  field_context: configData.field_context,
                  clause_extraction: configData.clause_extraction,
                  summarization_fields: configData.summarization_fields,
                  last_updated_by: configData.last_updated_by,
                  last_updated_on: configData.last_updated_on,
                  department: configData.department,
                  company_name: configData.company_name,
                  config_payload: configData.config_payload,
                  document_type: configData.document_type,
                  group_by_field: configData.group_by_field,
                  batch_process_exposure: configData.batch_process_exposure,
                  sharepoint_url: configData.sharepoint_url,
                  created_time: configData.created_time
                };
              }
            }
          )
        );

        try {
          const result = await queryFulfilled;

          dispatch(configApi.util.resetApiState());

          dispatch(
            configApi.endpoints.getAllConfigurations.initiate(undefined, {
              forceRefetch: true
            })
          );
        } catch (error) {
          patchResult.undo();
        }
      }
    }),
    deleteConfiguration: build.mutation<void, number>({
      queryFn: async (configId: number) => {
        try {
          const result = await ConfigService.deleteConfiguration(configId);

          // Return success
          return { data: undefined };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message
            }
          };
        }
      },
      invalidatesTags: [ApiCacheTags.CONFIGURATION_LIST]
    })
  })
});

export const {
  useGetAllConfigurationsQuery,
  useCreateConfigurationMutation,
  useUpdateConfigurationMutation,
  useDeleteConfigurationMutation
} = configApi;
